/**
 * Public assessee page — no auth, opens from emailed/copied link.
 * Just shows assessment, collects name+email, displays all questions, posts answers.
 */
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle, Sparkles, AlertCircle, ArrowLeft } from "lucide-react";
import axios from "axios";
import usePageTitle from "@/hooks/usePageTitle";
import { LogoLight } from "@/components/brand/Logo";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicAssessmentPage() {
  usePageTitle("Skills Assessment");
  const { token } = useParams();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/api/assess/${token}`)
      .then(r => setAssessment(r.data))
      .catch(err => setError(err?.response?.data?.detail || "Assessment not found"))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async () => {
    setError("");
    if (name.trim().length < 2 || !email.includes("@")) { setError("Enter your name and a valid email"); return; }
    const filled = Object.values(answers).filter(v => (v || "").trim().length >= 30).length;
    const required = assessment.skills.reduce((sum, s) => sum + s.questions.length, 0);
    if (filled < required) { setError(`Please answer all ${required} questions (min 30 characters each)`); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/assess/${token}/submit`, { full_name: name, email, role_applying_for: role, answers });
      setSubmitted(true);
    } catch (err) { setError(err?.response?.data?.detail || "Submit failed"); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen bg-cream flex items-center justify-center"><div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" /></div>;
  if (error && !assessment) return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-4" data-testid="assessee-invalid-token">
      <div className="bg-white rounded-2xl shadow-xl p-10 max-w-md text-center">
        <Link to="/" className="inline-block mb-6">
          <LogoLight width={180} height={42} />
        </Link>
        <div className="w-14 h-14 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-7 h-7 text-amber-600" />
        </div>
        <h2 className="font-playfair text-navy text-2xl font-bold mb-2">Link no longer active</h2>
        <p className="font-dm text-navy/60 text-sm mb-6">{error} The assessment may have closed or the link may be incorrect. Please reach out to whoever sent you this link for a new one.</p>
        <Link to="/" data-testid="invalid-token-home-link" className="inline-flex items-center gap-2 bg-navy text-gold font-dm font-bold text-sm px-5 py-2.5 rounded-lg hover:bg-navy/90 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to RoleIQ
        </Link>
      </div>
    </div>
  );
  if (submitted) return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-4" data-testid="assessee-success">
      <div className="bg-white rounded-2xl shadow-md p-10 max-w-md text-center">
        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
        <h2 className="font-playfair text-navy text-2xl font-bold mb-2">Submission received</h2>
        <p className="font-dm text-navy/60 text-sm">Thank you, {name}. The reviewer will be in touch.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-cream py-10" data-testid="public-assessment-page">
      {/* Branded public header */}
      <div className="bg-white" data-testid="public-assessment-header">
        <div className="max-w-3xl mx-auto px-8 py-6">
          <LogoLight width={180} height={42} />
        </div>
        <div style={{ height: "1px", background: "rgba(15,30,53,0.08)" }} />
      </div>
      <div className="max-w-3xl mx-auto px-4 mt-8">
        <div className="bg-white rounded-2xl shadow-md p-8 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-gold" />
            <span className="font-dm text-navy/50 text-[10px] uppercase tracking-wider">RoleIQ Assessment</span>
          </div>
          <h1 className="font-playfair text-navy text-3xl font-bold mb-2" style={{ fontSize: "18px" }}>{assessment.title}</h1>
          <p className="font-dm text-navy/60 text-sm">{assessment.description || `Role: ${assessment.role_title}`}</p>
        </div>
        <div className="bg-white rounded-2xl shadow-md p-6 mb-6">
          <p className="font-dm text-navy/70 text-xs font-bold mb-3">Your details</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Full name" className="border border-navy/15 rounded-lg px-3 py-2 font-dm text-sm" data-testid="assessee-name" />
            <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="Email" className="border border-navy/15 rounded-lg px-3 py-2 font-dm text-sm" data-testid="assessee-email" />
            <input value={role} onChange={e => setRole(e.target.value)} placeholder="Role you're applying for (optional)" className="border border-navy/15 rounded-lg px-3 py-2 font-dm text-sm sm:col-span-2" />
          </div>
        </div>
        {assessment.skills.map(s => (
          <div key={s.skill_code} className="bg-white rounded-2xl shadow-md p-6 mb-4">
            <p className="font-dm text-gold text-[10px] uppercase tracking-wider font-bold mb-1">{s.dimension}</p>
            <h3 className="font-playfair text-navy text-xl font-semibold mb-4">{s.skill_name}</h3>
            {s.questions.map(q => (
              <div key={q.id} className="mb-5">
                <div className="flex items-start gap-2 mb-2">
                  <span className="font-dm text-[10px] px-2 py-0.5 rounded-full bg-gold/10 text-gold font-bold">{q.question_type}</span>
                  <p className="font-dm text-navy text-sm flex-1">{q.question_text}</p>
                </div>
                <textarea value={answers[q.id] || ""} onChange={e => setAnswers(prev => ({...prev, [q.id]: e.target.value}))}
                  rows={4} placeholder={q.question_type === "STAR" ? "Cover Situation, Task, Action, Result — at least 30 characters" : "Your answer — at least 30 characters"}
                  data-testid={`answer-${q.id}`}
                  className="w-full border border-navy/15 rounded-lg px-3 py-2 font-dm text-sm focus:border-gold focus:outline-none" />
                <p className="font-dm text-navy/30 text-[10px] text-right mt-1">{(answers[q.id] || "").length} chars</p>
              </div>
            ))}
          </div>
        ))}
        {error && <p className="font-dm text-red-500 text-sm bg-red-50 rounded-lg p-3 mb-3">{error}</p>}
        <button onClick={submit} disabled={submitting} data-testid="assessee-submit"
          className="w-full bg-navy text-gold font-dm font-bold py-3 rounded-lg disabled:opacity-50">
          {submitting ? "Submitting..." : "Submit assessment"}
        </button>
      </div>
    </div>
  );
}
